import React from "react";
import { useTranslation } from "react-i18next";
import { getLegalUrls } from "../config/legalUrls";
import type { LegalConsentsPayload } from "@mvk/shared";

export type LegalConsentState = {
  userAgreement: boolean;
  pdProcessing: boolean;
  advertising: boolean;
  newsletter: boolean;
};

export const EMPTY_LEGAL_CONSENTS: LegalConsentState = {
  userAgreement: false,
  pdProcessing: false,
  advertising: false,
  newsletter: false,
};

export const ALL_LEGAL_CONSENTS: LegalConsentState = {
  userAgreement: true,
  pdProcessing: true,
  advertising: true,
  newsletter: true,
};

export function legalConsentsReady(state: LegalConsentState): boolean {
  return state.userAgreement && state.pdProcessing;
}

export function toLegalConsentsPayload(state: LegalConsentState): LegalConsentsPayload {
  return {
    userAgreement: state.userAgreement,
    pdProcessing: state.pdProcessing,
    advertising: state.advertising,
    newsletter: state.newsletter,
  };
}

type LegalConsentCheckboxesProps = {
  value: LegalConsentState;
  onChange: (next: LegalConsentState) => void;
  compact?: boolean;
};

function DocLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-indigo-600 underline underline-offset-2"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  );
}

export function LegalConsentCheckboxes({
  value,
  onChange,
  compact = false,
}: LegalConsentCheckboxesProps) {
  const { i18n } = useTranslation();
  const urls = getLegalUrls(i18n.language);

  /** UI shows one optional marketing checkbox; payload keeps both flags in sync. */
  const marketingChecked = value.advertising && value.newsletter;
  const allChecked = value.userAgreement && value.pdProcessing && marketingChecked;

  const toggleAll = (checked: boolean) => {
    onChange(checked ? { ...ALL_LEGAL_CONSENTS } : { ...EMPTY_LEGAL_CONSENTS });
  };

  const setMarketing = (checked: boolean) => {
    onChange({ ...value, advertising: checked, newsletter: checked });
  };

  const rowClass = `flex items-start gap-2.5 cursor-pointer ${compact ? "py-0.5" : "py-1"}`;
  const boxClass =
    "mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0";

  return (
    <div className={`space-y-1.5 ${compact ? "" : "pt-1"}`}>
      <label
        className={`flex items-start gap-2.5 cursor-pointer border border-indigo-100 bg-indigo-50/60 rounded-xl px-2.5 ${compact ? "py-1.5" : "py-2"}`}
      >
        <input
          type="checkbox"
          checked={allChecked}
          onChange={(e) => toggleAll(e.target.checked)}
          className={boxClass}
        />
        <span className="text-[12px] text-indigo-900 leading-snug font-medium">Выбрать все</span>
      </label>

      <label className={rowClass}>
        <input
          type="checkbox"
          checked={value.userAgreement}
          onChange={(e) => onChange({ ...value, userAgreement: e.target.checked })}
          className={boxClass}
        />
        <span className="text-[12px] text-gray-700 leading-snug">
          * Принимаю условия{" "}
          <DocLink href={urls.userAgreement}>Пользовательского соглашения</DocLink>
        </span>
      </label>

      <label className={rowClass}>
        <input
          type="checkbox"
          checked={value.pdProcessing}
          onChange={(e) => onChange({ ...value, pdProcessing: e.target.checked })}
          className={boxClass}
        />
        <span className="text-[12px] text-gray-700 leading-snug">
          * Согласен на{" "}
          <DocLink href={urls.pdConsent}>обработку персональных данных</DocLink> в соответствии с{" "}
          <DocLink href={urls.privacyPolicy}>Политикой</DocLink>
        </span>
      </label>

      <label className={rowClass}>
        <input
          type="checkbox"
          checked={marketingChecked}
          onChange={(e) => setMarketing(e.target.checked)}
          className={boxClass}
        />
        <span className="text-[12px] text-gray-700 leading-snug">
          Согласен на получение уведомлений и рекламных сообщений о выставках компании МВК (
          <DocLink href={urls.advertising}>подробнее</DocLink>)
        </span>
      </label>

      <p className="text-[11px] text-gray-400 pt-0.5">Обязательные пункты отмечены *</p>
    </div>
  );
}
